import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Checkbox } from '../../../../../../components/ui/checkbox';
import { Plus, Trash2, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { AntdDate } from '../../../../../../components/ui/antd-date';
import {
  updateMaterialTraceability,
  updateWarehouseMaterialTraceability,
} from '../../../../../../redux/Project/materialSaga';
import { isPipeLikeMaterialCategory } from '../materialCategoryConstants';
import { isWarehouseMaterialRow } from '../../../../../analysis/utils/warehouseMaterialPayload';
interface PipeSegment {
  id: string;
  segmentName: string;
  segmentLength: number;
  dateInventory: Date;
  used: boolean;
}

interface PipeSegmentationModalProps {
  isOpen: boolean;
  onClose: () => void;
  pipeId: string;
  heatNumber: string;
  pipeNo: string;
  onSave: (segments: PipeSegment[]) => void;
  selectedMaterial: any;
}

export const PipeSegmentationModal: React.FC<PipeSegmentationModalProps> = ({
  isOpen,
  onClose,
  pipeId,
  heatNumber,
  pipeNo,
  onSave,
  selectedMaterial
}) => {
  const defaultSegments = (): PipeSegment[] => [
    {
      id: '1',
      segmentName: 'A',
      segmentLength: 0,
      dateInventory: new Date(),
      used: false,
    },
  ];

  const parseSegments = (material: typeof selectedMaterial): PipeSegment[] => {
    const existingSegments = material?.details?.pipeSegmentation || defaultSegments();
    return existingSegments.map((segment: PipeSegment) => ({
      ...segment,
      dateInventory:
        segment.dateInventory instanceof Date
          ? segment.dateInventory
          : new Date(segment.dateInventory),
    }));
  };

  const [segments, setSegments] = useState<PipeSegment[]>(() => parseSegments(selectedMaterial));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && selectedMaterial) {
      setSegments(parseSegments(selectedMaterial));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedMaterial?.materialId]);

  const addSegment = () => {
    const newSegment: PipeSegment = {
      id: Date.now().toString(),
      segmentName: String.fromCharCode(65 + segments.length), // A, B, C, etc.
      segmentLength: 0,
      dateInventory: new Date(Date.now()),
      used: false
    };
    setSegments([...segments, newSegment]);
  };

  const removeSegment = (id: string) => {
    if (segments.length > 1) {
      setSegments(segments.filter((segment) => segment.id !== id));
    }
  };

  const updateSegment = (id: string, field: keyof PipeSegment, value: any) => {
    setSegments(segments.map(segment => 
      segment.id === id ? { ...segment, [field]: value } : segment
    ));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (isPipeLikeMaterialCategory(selectedMaterial.materialCategory)) {
        if (!selectedMaterial.details) {
          selectedMaterial.details = {};
        }
        selectedMaterial.details.pipeSegmentation = segments;
      }

      const detailsPipeNo = selectedMaterial?.details?.pipeNo;
      const resolvedPipeNo = String(
        detailsPipeNo ?? (pipeNo && pipeNo !== 'N/A' ? pipeNo : '')
      ).trim();
      const isWarehouse = isWarehouseMaterialRow(selectedMaterial as Record<string, unknown>);

      const apiPayload = isWarehouse
        ? {
            data: [
              {
                materialId: selectedMaterial.materialId,
                materialCategory: selectedMaterial.materialCategory || 'Pipe',
                isWarehouseInventory: true,
                projectId: null,
                pipeId: null,
                projectTypeId: null,
                details: {
                  pipeNo: resolvedPipeNo || 'N/A',
                  pipeSegmentation: segments,
                },
              },
            ],
          }
        : {
            data: [
              {
                materialId: selectedMaterial.materialId,
                materialCategory: selectedMaterial.materialCategory,
                details: {
                  pipeSegmentation: segments,
                },
              },
            ],
          };

      const result = isWarehouse
        ? await updateWarehouseMaterialTraceability(apiPayload)
        : await updateMaterialTraceability(apiPayload, '', true);

      if (result) {
        onSave(segments);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (id: string, dateString: string) => {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      updateSegment(id, 'dateInventory', date);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Pipe Segmentation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Header Information */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-gray-700">Pipe ID</Label>
              <div className="text-lg font-semibold text-gray-900">{pipeId}</div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Pipe Number</Label>
              <div className="text-lg font-semibold text-gray-900">{pipeNo}</div>
            </div>
          </div>

          {/* Segments Table */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Pipe Segments</h3>
              <Button
                onClick={addSegment}
                className="flex items-center gap-2"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                Add Segment
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-1/5">Segment Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-1/5">Segment Length</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-1/4">Date Inventory</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-1/6">Used</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-1/6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {segments.map((segment, index) => (
                    <tr key={segment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Input
                          value={segment.segmentName}
                          onChange={(e) => updateSegment(segment.id, 'segmentName', e.target.value)}
                          className="w-full min-w-[120px]"
                          placeholder="A"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          value={segment.segmentLength}
                          onChange={(e) => updateSegment(segment.id, 'segmentLength', parseFloat(e.target.value) || 0)}
                          className="w-full min-w-[140px]"
                          step="0.1"
                          placeholder="0.0"
                        />
                      </td>
                      <td className="px-4 py-3">
                           <div className="flex items-center gap-2">
                          
                           <AntdDate
                             type="date"
                             value={segment.dateInventory instanceof Date ? segment.dateInventory.toISOString().split('T')[0] : ''}
                             onChange={(date) => handleDateChange(segment.id, date?.toString() || '')}
                             className="w-full min-w-[160px]"
                           />
                         </div>
                      </td>
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={segment.used}
                          onCheckedChange={(checked) => updateSegment(segment.id, 'used', checked)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {segments.length > 1 && (
                          <Button
                            type="button"
                            onClick={() => removeSegment(segment.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}
            loading={loading} 
            loadingText="Saving..."
            >
              Save Segments
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
